import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FileText, Handshake, History, KeyRound, Loader2, Lock, LogIn, Package, Pencil, Plus, Trash2, Unlock, X, ClipboardCheck, Factory } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AdminClosures,
  StalledProducts,
  StockOverview,
} from "@/components/partner/AdminConsignment";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Field, Loading, PageHeader, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ManualMovementModal } from "@/components/ds/ManualMovementModal";
import { Switch } from "@/components/ui/switch";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { ProspectsPanel } from "@/components/partner/ProspectsPanel";
import {
  FulfillmentModal,
  ItemFulfillmentLine,
  fulfillmentText,
} from "@/components/partner/FulfillmentModal";
import { NoticesPanel } from "@/components/partner/NoticesPanel";

const REP_STATUS = {
  SOLICITADA: "Pendente",
  APROVADA: "Aprovada",
  EM_PRODUCAO: "Em produção",
  PRODUCAO_CONCLUIDA: "Produção concluída",
  SEPARADA: "Embalando",
  ENVIADA: "Pronta / enviada",
  RECEBIDA: "Concluída",
  CANCELADA: "Cancelada",
};
const RET_STATUS = {
  SOLICITADA: "Pendente",
  APROVADA: "Aprovada",
  EM_TRANSPORTE: "Em transporte",
  RECEBIDA: "Recebida",
  RECUSADA: "Recusada",
  CANCELADA: "Cancelada",
};

const TONE = {
  ok: "text-emerald-300",
  info: "text-brand",
  warn: "text-amber-300",
  bad: "text-red-300",
};

// Situação da reposição em uma frase. Lê só o que a API já envia (status, has_shortage,
// stock_applied/sent_at). Não recalcula estoque, não cria status e não muda nada.
const repSituation = (r) => {
  switch (r.status) {
    case "SOLICITADA":
      return { tone: "warn", text: "Solicitação de reposição recebida. Aguardando aprovação para iniciar o atendimento." };
    case "APROVADA":
      return r.has_shortage
        ? { tone: "warn", text: "Reposição aprovada. Alguns produtos precisam ser produzidos antes do envio." }
        : { tone: "ok", text: "Reposição aprovada. Os produtos estão disponíveis para separação." };
    case "EM_PRODUCAO":
      return { tone: "info", text: "Os produtos desta reposição estão em produção. Confira os detalhes na aba Produção." };
    case "PRODUCAO_CONCLUIDA":
      return { tone: "info", text: "Produção concluída. Os produtos estão prontos para a próxima etapa da reposição." };
    case "SEPARADA":
      return { tone: "info", text: "Reposição em preparação. Os produtos estão sendo separados e embalados para o parceiro." };
    case "ENVIADA":
      // sent_at/stock_applied só existem quando o envio realmente aconteceu.
      return r.stock_applied || r.sent_at
        ? { tone: "ok", text: "Reposição enviada ao parceiro. O estoque consignado foi atualizado conforme o envio." }
        : { tone: "info", text: "Reposição pronta para envio. Próximo passo: enviar ao parceiro." };
    case "RECEBIDA":
      return { tone: "ok", text: "Reposição concluída. Os produtos foram enviados e registrados no estoque consignado do parceiro." };
    case "CANCELADA":
      return { tone: "bad", text: "Solicitação de reposição cancelada. Nenhum novo envio será realizado." };
    default:
      return null;
  }
};

const badgeCls = (status) =>
  ({
    SOLICITADA: "border-amber-500/40 bg-amber-500/15 text-amber-300",
    APROVADA: "border-sky-500/40 bg-sky-500/15 text-sky-300",
    EM_PRODUCAO: "border-sky-500/40 bg-sky-500/15 text-sky-300",
    PRODUCAO_CONCLUIDA: "border-brand/40 bg-brand/15 text-brand",
    SEPARADA: "border-brand/40 bg-brand/15 text-brand",
    ENVIADA: "border-brand/40 bg-brand/15 text-brand",
    RECEBIDA: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
    CANCELADA: "border-destructive/40 bg-destructive/15 text-red-300",
    RECUSADA: "border-destructive/40 bg-destructive/15 text-red-300",
  }[status] || "border-white/15 bg-secondary/60 text-muted-foreground");

export default function Partners() {
  const [partners, setPartners] = useState(null);
  const [products, setProducts] = useState([]);
  const [kits, setKits] = useState([]);
  const [cats, setCats] = useState([]);
  const [reps, setReps] = useState([]);
  const [rets, setRets] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [passwordFor, setPasswordFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [blockFor, setBlockFor] = useState(null);
  const [converting, setConverting] = useState(null);
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  // Visualização administrativa: minha sessão admin é guardada e restaurada depois.
  const enterPartnerArea = async (partner) => {
    try {
      await startImpersonation(partner.id);
      toast.success(`Visualização administrativa de ${partner.name}.`);
      navigate("/parceiro");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startConversion = (prospect) => {
    setConverting(prospect);
    setEditing("new");
  };

  const closeEditing = () => {
    setEditing(null);
    setConverting(null);
  };

  const afterPartnerSaved = async () => {
    if (converting) {
      try {
        await api.put(`/prospects/${converting.id}/status`, { status: "VIROU_PARCEIRO" });
      } catch (e) {
        toast.error(apiError(e));
      }
      setConverting(null);
    }
    load();
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, pr, rp, rt, kt, ct] = await Promise.all([
        api.get("/partners"),
        api.get("/products"),
        api.get("/replenishments"),
        api.get("/returns"),
        api.get("/kits"),
        api.get("/categories"),
      ]);
      setPartners(p.data);
      setProducts(pr.data);
      setReps(rp.data);
      setRets(rt.data);
      setKits(kt.data);
      setCats(ct.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (kind, id, status) => {
    try {
      await api.put(`/${kind}/${id}/status`, { status });
      toast.success("Solicitação atualizada.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/partners/${toDelete.id}`);
      toast.success("Parceiro excluído.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setToDelete(null);
    }
  };

  // Bloqueio/liberação MANUAL de novas reposições. Não mexe em mais nada do parceiro.
  const confirmBlock = async () => {
    const blocking = !blockFor?.replenish_lock?.blocked;
    try {
      await api.put(`/partners/${blockFor.id}/replenishment-block`, { blocked: blocking });
      toast.success(blocking ? "Reposições bloqueadas." : "Reposições liberadas.");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBlockFor(null);
    load();
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (partners === null) return <Loading label="Carregando parceiros..." />;

  return (
    <div className="space-y-8" data-testid="partners-page">
      <PageHeader
        title="Parceiros"
        subtitle="Cada parceiro tem login próprio, catálogo liberado por você e estoque consignado separado do seu."
        action={
          <Button
            onClick={() => setEditing("new")}
            data-testid="new-partner-btn"
            className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
          >
            <Plus className="h-4 w-4" /> Novo Parceiro
          </Button>
        }
      />

      <Tabs defaultValue="parceiros">
        <TabsList className="flex h-auto w-full flex-wrap gap-1 bg-secondary/60">
          <TabsTrigger value="parceiros" data-testid="tab-partners">
            Parceiros
          </TabsTrigger>
          <TabsTrigger value="reposicoes" data-testid="tab-replenishments">
            Reposições ({reps.filter((r) => r.status === "SOLICITADA").length})
          </TabsTrigger>
          <TabsTrigger value="devolucoes" data-testid="tab-returns">
            Devoluções ({rets.filter((r) => r.status === "SOLICITADA").length})
          </TabsTrigger>
          <TabsTrigger value="fechamentos" data-testid="tab-closures">
            Fechamentos
          </TabsTrigger>
          <TabsTrigger value="estoque" data-testid="tab-stock-overview">
            Onde está o estoque
          </TabsTrigger>
          <TabsTrigger value="parados" data-testid="tab-stalled">
            Produtos parados
          </TabsTrigger>
          <TabsTrigger value="prospeccoes" data-testid="tab-prospects">
            Prospecções
          </TabsTrigger>
          <TabsTrigger value="comunicacao" data-testid="tab-notices">
            Comunicação
          </TabsTrigger>
        </TabsList>

        <TabsContent value="parceiros" className="mt-6">
          {partners.length === 0 ? (
            <EmptyState
              icon={Handshake}
              title="Nenhum parceiro cadastrado"
              description="Cadastre a primeira loja parceira e defina o login e os preços de consignação."
              action={
                <Button onClick={() => setEditing("new")} data-testid="empty-new-partner-btn" className="gap-2 rounded-full">
                  <Plus className="h-4 w-4" /> Novo Parceiro
                </Button>
              }
            />
          ) : (
            <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3">
              {partners.map((p) => (
                <Surface key={p.id} className="min-w-0 overflow-hidden p-5" data-testid={`partner-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-display text-base font-medium" data-testid={`partner-name-${p.id}`}>
                        {p.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{p.email}</p>
                      {p.contact && <p className="truncate text-xs text-muted-foreground">{p.contact}</p>}
                    </div>
                    <span
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${
                        p.active
                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                          : "border-white/15 bg-secondary/60 text-muted-foreground"
                      }`}
                    >
                      {p.active ? "Ativo" : "Inativo"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-3">
                      <p className="truncate text-xs text-muted-foreground">Consignado</p>
                      <p className="font-num text-lg" data-testid={`partner-consigned-${p.id}`}>
                        {Number(p.consigned_units || 0).toLocaleString("pt-BR")} un
                      </p>
                    </div>
                    <div className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-3">
                      <p className="truncate text-xs text-muted-foreground">Produtos liberados</p>
                      <p className="font-num text-lg">{p.products_count}</p>
                    </div>
                  </div>

                  {p.replenish_lock?.blocked ? (
                    <p
                      className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                      data-testid={`partner-blocked-${p.id}`}
                    >
                      🔒 Reposições bloqueadas
                    </p>
                  ) : (
                    <p className="mt-3 text-xs text-emerald-300"
                       data-testid={`partner-released-${p.id}`}>
                      ✓ Reposições liberadas
                    </p>
                  )}
                  {p.replenish_lock?.pending && (
                    <p
                      className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
                      data-testid={`partner-pending-${p.id}`}
                    >
                      ⚠️ Fechamento de {p.replenish_lock.period_label}{" "}
                      {p.replenish_lock.overdue
                        ? `em atraso desde ${dataBR(p.replenish_lock.regularize_until)}`
                        : `pendente · regularizar até ${dataBR(p.replenish_lock.regularize_until)}`}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap justify-end gap-1 border-t border-white/10 pt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBlockFor(p)}
                      data-testid={`partner-replenish-block-btn-${p.id}`}
                      className={`gap-1.5 text-xs ${
                        p.replenish_lock?.blocked
                          ? "text-emerald-300 hover:text-emerald-200"
                          : "text-muted-foreground hover:text-red-300"
                      }`}
                    >
                      {p.replenish_lock?.blocked ? (
                        <>
                          <Unlock className="h-3.5 w-3.5" /> Liberar reposições
                        </>
                      ) : (
                        <>
                          <Lock className="h-3.5 w-3.5" /> Bloquear reposições
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDetailFor(p)}
                      data-testid={`partner-detail-btn-${p.id}`}
                      className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <History className="h-3.5 w-3.5" /> Estoque e histórico
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => enterPartnerArea(p)}
                      data-testid={`partner-impersonate-btn-${p.id}`}
                      className="gap-1.5 text-xs text-muted-foreground hover:text-brand"
                    >
                      <LogIn className="h-3.5 w-3.5" /> Acessar área do parceiro
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPasswordFor(p)}
                      data-testid={`partner-password-btn-${p.id}`}
                      className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Senha
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(p)}
                      data-testid={`partner-edit-btn-${p.id}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToDelete(p)}
                      data-testid={`partner-delete-btn-${p.id}`}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Surface>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reposicoes" className="mt-6">
          <RequestList
            list={reps}
            statuses={REP_STATUS}
            kind="replenishments"
            onStatus={setStatus}
            onReload={load}
            emptyText="Nenhuma solicitação de reposição recebida."
            hint="O seu estoque só é baixado quando você marca a reposição como Enviada."
          />
        </TabsContent>

        <TabsContent value="devolucoes" className="mt-6">
          <RequestList
            list={rets}
            statuses={RET_STATUS}
            kind="returns"
            onStatus={setStatus}
            emptyText="Nenhuma solicitação de devolução recebida."
            hint="Os estoques só mudam quando você marca a devolução como Recebida."
          />
        </TabsContent>

        <TabsContent value="fechamentos" className="mt-6">
          <AdminClosures />
        </TabsContent>

        <TabsContent value="estoque" className="mt-6">
          <StockOverview />
        </TabsContent>

        <TabsContent value="parados" className="mt-6">
          <StalledProducts />
        </TabsContent>

        <TabsContent value="prospeccoes" className="mt-6">
          <ProspectsPanel onConvert={startConversion} />
        </TabsContent>
        <TabsContent value="comunicacao" className="mt-6">
          <NoticesPanel partners={partners} />
        </TabsContent>
      </Tabs>

      {editing && (
        <PartnerModal
          partner={editing === "new" ? null : editing}
          prefill={
            converting
              ? {
                  name: converting.nome_comercio || "",
                  contact: converting.whatsapp || "",
                  notes: [
                    converting.responsavel ? `Responsável: ${converting.responsavel}` : "",
                    [converting.cidade, converting.estado].filter(Boolean).join(" / "),
                    converting.instagram ? `Instagram: ${converting.instagram}` : "",
                    converting.observacoes || "",
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }
              : null
          }
          products={products}
          kits={kits}
          cats={cats}
          onClose={closeEditing}
          onSaved={afterPartnerSaved}
        />
      )}
      {passwordFor && <PasswordModal partner={passwordFor} onClose={() => setPasswordFor(null)} />}
      {detailFor && <PartnerDetailModal partner={detailFor} onClose={() => setDetailFor(null)} />}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-partner-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir parceiro?</AlertDialogTitle>
            <AlertDialogDescription>
              O login de <strong>{toDelete?.name}</strong> e o histórico de consignação serão
              removidos. Só é possível excluir se não houver produtos com o parceiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-partner-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete-partner-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!blockFor} onOpenChange={(o) => !o && setBlockFor(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="replenish-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {blockFor?.replenish_lock?.blocked
                ? "Liberar novas reposições para este parceiro?"
                : "Bloquear novas reposições para este parceiro?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {blockFor?.replenish_lock?.blocked
                ? `${blockFor?.name} voltará a poder solicitar novas reposições imediatamente.`
                : "O parceiro continuará com acesso à conta, estoque, vendas, fechamentos e demais funcionalidades. Apenas novas solicitações de reposição ficarão bloqueadas."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-replenish-block-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBlock}
              data-testid="confirm-replenish-block-btn"
              className={
                blockFor?.replenish_lock?.blocked
                  ? ""
                  : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              }
            >
              {blockFor?.replenish_lock?.blocked ? "Liberar reposições" : "Bloquear reposições"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

function RequestList({ list, statuses, kind, onStatus, onReload, emptyText, hint }) {
  const [analyzing, setAnalyzing] = useState(null);
  const [sending, setSending] = useState(null);
  const sendToProduction = async (r) => {
    setSending(r.id);
    try {
      const { data } = await api.post(`/replenishments/${r.id}/send-to-production`);
      toast.success(data.message);
      onReload?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(null);
    }
  };
  const pdf = async (path) => {
    try {
      await openPdf(path);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (list.length === 0)
    return <EmptyState icon={Package} title="Nada por aqui" description={emptyText} />;
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        {list.map((r) => (
          <Surface key={r.id} className="p-5" data-testid={`${kind}-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-display text-base font-medium">{r.partner_name}</p>
                <p className="text-xs text-muted-foreground">
                  {r.number ? `${r.number} · ` : ""}
                  {dataBR(r.created_at)}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${badgeCls(r.status)}`}
                data-testid={`${kind}-status-${r.id}`}
              >
                {statuses[r.status] || r.status}
              </span>
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {r.items.map((it, idx) => (
                <li key={`${it.product_id}-${it.variation_id || idx}`}>
                  <div className="flex min-w-0 justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-num">{it.quantity}x</span> {it.name}
                    </span>
                    <span
                      className={`shrink-0 font-num text-xs ${it.missing > 0 ? "text-red-300" : "text-muted-foreground"}`}
                      data-testid={`${kind}-availability-${r.id}-${it.product_id}${
                        it.variation_id ? `-${it.variation_id}` : ""
                      }`}
                    >
                      {it.available != null
                        ? `disp. ${it.available}${it.missing > 0 ? ` · falta ${it.missing}` : ""}`
                        : `${brl(it.consign_price)} / un`}
                    </span>
                  </div>
                  {kind === "replenishments" && (
                    <ItemFulfillmentLine
                      item={it}
                      testid={`replenishment-fulfillment-${r.id}-${it.product_id}`}
                    />
                  )}
                </li>
              ))}
            </ul>
            {r.has_shortage && !["CANCELADA", "RECEBIDA", "RECUSADA", "ENVIADA"].includes(r.status) && (
              <p
                className="mt-2 rounded-lg border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-red-300"
                data-testid={`${kind}-shortage-${r.id}`}
              >
                {kind === "rep" && r.productions
                  ? `Falta estoque: ${r.productions} produção(ões) já foram criadas para as unidades faltantes${
                      r.productions_open ? ` · ${r.productions_open} em aberto` : " · todas concluídas"
                    }.`
                  : "Falta estoque para atender tudo. Ajuste as quantidades com o parceiro ou recuse."}
              </p>
            )}
            {r.reason && <p className="mt-2 text-xs text-muted-foreground">Motivo: {r.reason}</p>}
            {r.notes && <p className="mt-2 text-xs text-muted-foreground">Obs.: {r.notes}</p>}
            {kind === "replenishments" && (
              <>
                {r.fulfillment?.analyzed && (
                  <p
                    className="mt-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2 text-xs"
                    data-testid={`replenishment-fulfillment-summary-${r.id}`}
                  >
                    {fulfillmentText(r)}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pdf(`/replenishments/${r.id}/pdf`)}
                    data-testid={`replenishment-pdf-btn-${r.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs"
                  >
                    <FileText className="h-3.5 w-3.5" /> Gerar PDF
                  </Button>
                  {r.fulfillment_editable && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAnalyzing(r)}
                        data-testid={`replenishment-fulfillment-btn-${r.id}`}
                        className="h-9 gap-1.5 rounded-full text-xs"
                      >
                        <ClipboardCheck className="h-3.5 w-3.5" /> Analisar atendimento
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={sending === r.id}
                        onClick={() => sendToProduction(r)}
                        data-testid={`replenishment-send-production-btn-${r.id}`}
                        className="h-9 gap-1.5 rounded-full text-xs"
                      >
                        <Factory className="h-3.5 w-3.5" />
                        {sending === r.id ? "Enviando..." : "Enviar para produção"}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
            <div className="mt-4 border-t border-white/10 pt-3">
              {["CANCELADA", "RECEBIDA", "RECUSADA"].includes(r.status) ? (
                <p className="text-xs text-muted-foreground" data-testid={`${kind}-closed-${r.id}`}>
                  Encerrada como {statuses[r.status]} — não é possível voltar ao fluxo.
                </p>
              ) : (
                <Select value={r.status} onValueChange={(v) => v !== r.status && onStatus(kind, r.id, v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`${kind}-status-select-${r.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-popover">
                    {Object.entries(statuses).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="text-xs">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {kind === "replenishments" && repSituation(r) && (
              <p
                className={`mt-3 text-xs ${TONE[repSituation(r).tone]}`}
                data-testid={`replenishment-situation-${r.id}`}
              >
                {repSituation(r).text}
              </p>
            )}
          </Surface>
        ))}
      </div>
      {analyzing && (
        <FulfillmentModal
          request={analyzing}
          onClose={() => setAnalyzing(null)}
          onSaved={onReload}
        />
      )}
    </div>
  );
}

function PartnerModal({ partner, prefill = null, products, kits = [], cats = [], onClose, onSaved }) {
  const [form, setForm] = useState(
    partner
      ? { ...partner, categories: partner.categories || [] }
      : {
          name: prefill?.name || "",
          contact: prefill?.contact || "",
          email: "",
          notes: prefill?.notes || "",
          active: true,
          products: [],
          categories: [],
        }
  );
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState({ category: "", mode: "PERCENT", value: "", onlyMissing: false });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();
  // Produtos e Kits na mesma lista de liberação — o kit é identificado pelo kind.
  const releasable = [
    ...products.map((p) => ({ ...p, kind: "PRODUTO" })),
    ...kits.map((k) => ({ ...k, kind: "KIT", category: "Kit" })),
  ];

  const applyBulk = () => {
    const value = Number(bulk.value || 0);
    if (!value) {
      toast.error("Informe o valor ou a porcentagem.");
      return;
    }
    const target = products.filter((p) => !bulk.category || p.category === bulk.category);
    if (target.length === 0) {
      toast.error("Nenhum produto nesta categoria.");
      return;
    }
    setForm((f) => {
      const byId = new Map(f.products.map((p) => [p.product_id, { ...p }]));
      target.forEach((prod) => {
        const current = byId.get(prod.id);
        if (bulk.onlyMissing && current && Number(current.consign_price || 0) > 0) return;
        const price =
          bulk.mode === "PERCENT"
            ? Math.round(Number(prod.price || 0) * value) / 100
            : value;
        byId.set(prod.id, { product_id: prod.id, consign_price: price });
      });
      return { ...f, products: [...byId.values()] };
    });
    toast.success(
      `Preço aplicado em ${target.length} produto(s)${bulk.category ? ` de ${bulk.category}` : ""}.`
    );
  };

  const toggleCategory = (id) => {
    setForm((f) => {
      const list = f.categories || [];
      return {
        ...f,
        categories: list.includes(id) ? list.filter((c) => c !== id) : [...list, id],
      };
    });
  };

  const toggleProduct = (product) => {
    setForm((f) => {
      const exists = f.products.find((p) => p.product_id === product.id);
      return {
        ...f,
        products: exists
          ? f.products.filter((p) => p.product_id !== product.id)
          : [...f.products, { product_id: product.id, consign_price: product.price || 0,
                              kind: product.kind || "PRODUTO" }],
      };
    });
  };

  const setPrice = (productId, value) =>
    setForm((f) => ({
      ...f,
      products: f.products.map((p) =>
        p.product_id === productId ? { ...p, consign_price: value } : p
      ),
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      name: form.name,
      contact: form.contact || "",
      email: form.email,
      notes: form.notes || "",
      active: !!form.active,
      categories: form.categories || [],
      products: form.products.map((p) => ({
        product_id: p.product_id,
        consign_price: Number(p.consign_price || 0),
        kind: p.kind || "PRODUTO",
      })),
    };
    try {
      if (partner) await api.put(`/partners/${partner.id}`, body);
      else await api.post(`/partners?password=${encodeURIComponent(password)}`, body);
      toast.success(partner ? "Parceiro atualizado." : "Parceiro cadastrado com login criado.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={partner ? "Editar Parceiro" : "Novo Parceiro"}
      description="Libere as categorias que ele pode trabalhar. Os preços vêm do produto."
      testid="partner-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome do parceiro">
            <Input value={form.name} onChange={set("name")} required placeholder="Ex.: Loja XYZ" data-testid="partner-name-input" />
          </Field>
          <Field label="Contato">
            <Input value={form.contact || ""} onChange={set("contact")} placeholder="WhatsApp ou telefone" data-testid="partner-contact-input" />
          </Field>
          <Field label="E-mail de acesso" className={partner ? "sm:col-span-2" : ""}>
            <Input type="email" value={form.email} onChange={set("email")} required placeholder="loja@email.com" data-testid="partner-email-input" />
          </Field>
          {!partner && (
            <Field label="Senha inicial" hint="Mínimo 6 caracteres">
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="partner-password-input"
              />
            </Field>
          )}
        </div>

        <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="partner-categories-block">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Categorias disponíveis
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Libere as categorias que este parceiro pode trabalhar. Ele escolhe os modelos na área
              dele e os preços vêm do produto (público, consignado e lojista).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[...cats.map((c) => ({ id: c.id, name: c.name })),
              { id: "KITS", name: "Kits" }].map((c) => {
              const on = (form.categories || []).includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategory(c.id)}
                  data-testid={`partner-category-${c.id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    on ? "border-brand bg-brand/15 text-brand" : "border-white/15 text-muted-foreground"
                  }`}
                >
                  {on ? "✓ " : ""}
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        <details className="rounded-lg border border-white/10 bg-secondary/30 p-4" data-testid="partner-legacy-pricing">
          <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Preços específicos deste parceiro (opcional)
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Só use para exceções: o preço aqui sobrepõe o preço consignado do produto. Os valores
            já cadastrados foram mantidos.
          </p>
        <div className="mt-3 space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="bulk-pricing-block">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Precificar em massa
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Libera e precifica vários produtos de uma vez — ideal quando a categoria toda tem a
              mesma regra (ex.: 60% do preço público para todos os chaveiros).
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Categoria">
              <NativeSelect
                value={bulk.category}
                onChange={(v) => setBulk((b) => ({ ...b, category: v }))}
                placeholder="Todos os produtos"
                data-testid="bulk-category-select"
                options={categories.map((c) => ({ value: c, label: c }))}
              />
            </Field>
            <Field label="Regra">
              <NativeSelect
                value={bulk.mode}
                onChange={(v) => setBulk((b) => ({ ...b, mode: v }))}
                data-testid="bulk-mode-select"
                options={[
                  { value: "PERCENT", label: "% do preço público" },
                  { value: "FIXED", label: "Valor fixo (R$)" },
                ]}
              />
            </Field>
            <Field label={bulk.mode === "PERCENT" ? "Porcentagem" : "Valor por unidade"}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={bulk.value}
                onChange={(e) => setBulk((b) => ({ ...b, value: e.target.value }))}
                placeholder={bulk.mode === "PERCENT" ? "60" : "9,90"}
                className="font-num"
                data-testid="bulk-value-input"
              />
            </Field>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs">
              <Switch
                checked={bulk.onlyMissing}
                onCheckedChange={(v) => setBulk((b) => ({ ...b, onlyMissing: v }))}
                data-testid="bulk-only-missing-switch"
              />
              Não mexer em quem já tem preço
            </label>
            <Button
              type="button"
              variant="soft"
              onClick={applyBulk}
              data-testid="bulk-apply-btn"
              className="h-9 rounded-full px-4 text-xs"
            >
              Aplicar preço
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Preço específico por produto ou kit
          </span>
          {releasable.length === 0 ? (
            <p className="rounded-lg border border-dashed border-white/15 px-4 py-6 text-center text-sm text-muted-foreground">
              Cadastre produtos primeiro.
            </p>
          ) : (
            <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {releasable.map((prod) => {
                const selected = form.products.find((p) => p.product_id === prod.id);
                return (
                  <li
                    key={prod.id}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 ${
                      selected ? "border-primary/50 bg-primary/10" : "border-white/10 bg-secondary/40"
                    }`}
                    data-testid={`partner-product-${prod.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleProduct(prod)}
                      data-testid={`partner-product-toggle-${prod.id}`}
                      className="min-w-0 flex-1 text-left"
                    >
                      <p className="truncate text-sm">
                        {prod.name}
                        {prod.kind === "KIT" && (
                          <span
                            className="ml-1.5 rounded-full border border-primary/40 px-1.5 text-[10px] uppercase text-brand"
                            data-testid={`partner-kit-badge-${prod.id}`}
                          >
                            kit
                          </span>
                        )}
                      </p>
                      <p className="font-num text-xs text-muted-foreground">
                        Público {brl(prod.price)}
                      </p>
                    </button>
                    {selected ? (
                      <>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={selected.consign_price}
                          onChange={(e) => setPrice(prod.id, e.target.value)}
                          className="h-9 w-24 font-num"
                          data-testid={`partner-product-price-${prod.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => toggleProduct(prod)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleProduct(prod)}
                        className="h-8 rounded-full px-3 text-[11px]"
                      >
                        Liberar
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        </details>
        <Field label="Observações">
          <Textarea value={form.notes || ""} onChange={set("notes")} rows={2} data-testid="partner-notes-input" />
        </Field>

        <Button type="submit" disabled={busy} data-testid="save-partner-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
        </Button>
      </form>
    </ResponsiveModal>
  );
}

function PasswordModal({ partner, onClose }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/partners/${partner.id}/password`, { password });
      toast.success("Senha atualizada. Informe ao parceiro.");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nova senha de acesso"
      description={`${partner.name} · ${partner.email}`}
      testid="partner-password-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Senha" hint="Mínimo 6 caracteres">
          <Input
            type="text"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="new-partner-password-input"
          />
        </Field>
        <Button type="submit" disabled={busy} data-testid="save-partner-password-btn" className="h-11 rounded-full font-semibold">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar senha
        </Button>
      </form>
    </ResponsiveModal>
  );
}

function PartnerDetailModal({ partner, onClose }) {
  const [stock, setStock] = useState(null);
  const [moves, setMoves] = useState([]);
  const [moving, setMoving] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/partners/${partner.id}/stock`),
      api.get(`/partners/${partner.id}/movements`),
    ])
      .then(([s, m]) => {
        setStock(s.data);
        setMoves(m.data);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [partner.id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Estoque consignado"
      description={partner.name}
      testid="partner-detail-modal"
    >
      {stock === null ? (
        <Loading label="Carregando..." />
      ) : (
        <div className="mt-2 space-y-5">
          {stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum produto com este parceiro.</p>
          ) : (
            <ul className="divide-y divide-white/10" data-testid="partner-consigned-list">
              {stock.map((s) => (
                <li key={s.key || s.product_id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{s.name}</p>
                    <p className="font-num text-xs text-muted-foreground">
                      {brl(s.consign_price)} / un
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="font-num text-sm">
                      {Number(s.quantity).toLocaleString("pt-BR")} un
                    </span>
                    <Button
                      type="button"
                      variant="soft"
                      onClick={() => setMoving(s)}
                      data-testid={`partner-move-stock-${s.key || s.product_id}`}
                      className="h-8 rounded-full px-3 text-[11px]"
                    >
                      Movimentar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Movimentações
            </p>
            {moves.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma movimentação ainda.</p>
            ) : (
              <ul className="divide-y divide-white/10">
                {moves.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 py-2.5">
                    <span className="w-14 shrink-0 font-num text-xs text-muted-foreground">
                      {dataBR(m.created_at)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{m.product_name}</p>
                      <p className="text-xs text-muted-foreground">{m.type_label}</p>
                    </div>
                    <span className={`font-num text-sm ${m.quantity >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {m.quantity >= 0 ? "+" : "−"}
                      {Math.abs(m.quantity).toLocaleString("pt-BR")} un
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {moving && (
        <ManualMovementModal
          partner={partner}
          preset={{ product_id: moving.product_id, variation_id: moving.variation_id }}
          onClose={() => setMoving(null)}
          onSaved={load}
        />
      )}
    </ResponsiveModal>
  );
}
